import React, { useState } from "react";
import Breadcrumb from "../../../../Components/Common/Breadcrumb";
import { faAdd } from "@fortawesome/free-solid-svg-icons";
import Tabs from "../../../../Components/Common/Tabs";
import { liveClassTabsData } from "../../../../data/SidebarData";
import Table from "../../../../Components/Common/Table";
import TableHead from "../../../../Components/Common/TableHead";
import useSWR from "swr";
import { liveClassSubsUrls } from "../../../../Api/Auth/ApiUrls";
import QueryString from "qs";
import useTable from "../../../../hooks/useTable";
import { Loader } from "../../../../Components/Common/Loader";
import NoData from "../../../../Components/Common/NoData";
import LiveClassesRow from "./Components/LiveClassesRow";
import Pagination from "../../../../Components/Common/Pagination/Pagination";
import { useLocation } from "react-router-dom";
import useToastError from "../../../../hooks/useErrorToast";
import Modal from "../../../../Components/Common/Modal";
import useSWRMutation from "swr/mutation";
import {
  sendPostRequestAuth,
  sendPutRequestAuth,
} from "../../../../Api/swrConfig";
import { toast } from "react-toastify";
import LiveClassesForm from "./Components/LiveClassesForm";
import Storage from "../../Storage";

const tableColumnData = [
  { id: "title", label: "Title" },
  { id: "category", label: "Category" },
  { id: "startDate", label: "Start Date" },
  { id: "endDate", label: "End Date" },
  { id: "price", label: "Price" },
  { id: "createdAt", label: "Created" },
];

const LiveClasses = () => {
  const [row, setRow] = useState(null);
  const [errors, setErrors] = useState({});
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    category: null,
    startDate: "",
    endDate: "",
    price: 0,
    image: "",
    routine: "",
    actionButton: "",
  });
  const [contentType, setContentType] = useState(null);
  const [isModalOpen, setModalOpen] = useState(false);
  const [isStorageModalOpen, setStorageModalOpen] = useState(false);
  const { showErrorToast } = useToastError();
  const location = useLocation();

  const { page, rowsPerPage, handleChangePage, handleChangeRowsPerPage } =
    useTable({ defaultOrderBy: "" });
  const apiQueryParams = {
    offset: (page - 1) * rowsPerPage,
    limit: rowsPerPage,
  };

  const apiQueryString = QueryString.stringify(apiQueryParams);
  const { isLoading, data, mutate } = useSWR(
    liveClassSubsUrls.list(apiQueryString)
  );

  const handleInputChange = (name, value) => {
    setFormData((prevState) => ({
      ...prevState,
      [name]: value, // Set only the field that's changed
    }));
  };
  const onEditClick = (row) => {
    setRow(row);
    handleInputChange("title", row.title);
    handleInputChange("description", row.description);
    handleInputChange("startDate", row.startDate);
    handleInputChange("endDate", row.endDate);
    handleInputChange("price", row.price);
    handleInputChange("category", row.category?._id);
    handleInputChange("actionButton", row.actionButton);
    handleInputChange("image", row.image);
    handleInputChange("routine", row.routine);
    setModalOpen(true);
  };

  const { trigger, isMutating } = useSWRMutation(
    liveClassSubsUrls.create,
    sendPostRequestAuth,
    {
      onSuccess: (data) => {
        toast.success("Success");
        mutate();
        setModalOpen(false);
      },
      onError: (error) => {
        showErrorToast(error);
      },
    }
  );
  const { trigger: EditTrigger, isMutating: editMutating } = useSWRMutation(
    liveClassSubsUrls.edit(row?._id),
    sendPutRequestAuth,
    {
      onSuccess: (data) => {
        toast.success("Success");
        mutate();
        setModalOpen(false);
      },
      onError: (error) => {
        showErrorToast(error);
      },
    }
  );

  const actionButtons = [
    {
      label: "Create",
      icon: faAdd,
      onClick: () => {
        setRow(null);
        setFormData({
          title: "",
          description: "",
          category: null,
          startDate: "",
          endDate: "",
          price: 0,
          image: "",
          routine: "",
          actionButton: "",
        });
        setModalOpen(true);
      },
    },
  ];

  const isValidDateRange = (startDate, endDate) => {
    if (!startDate || !endDate) return false; // Ensure both dates exist

    const start = new Date(startDate);
    const end = new Date(endDate);

    return start <= end; // Return true if start is before or equal to end
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.title) newErrors.title = "Title is required";
    if (!formData.category) newErrors.category = "Category is required";
    if (!formData.startDate) newErrors.startDate = "Start Date is required";
    else if (!isValidDateRange(formData.startDate, formData.endDate))
      newErrors.startDate = "Start Date and End Date is not valid range";
    if (!formData.endDate) newErrors.endDate = "End Date is required";
    else if (!isValidDateRange(formData.startDate, formData.endDate))
      newErrors.endDate = "Start Date and End Date is not valid range";
    if (!formData.price && formData.price !== 0)
      newErrors.price = "Price is required";
    if (!formData.image) newErrors.image = "Image is required";
    if (!formData.routine) newErrors.routine = "Routine is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validateForm()) return;
    const modifiedData = {
      ...formData,
      category:
        typeof formData.category === "object"
          ? formData.category.value
          : formData.category,
      startDate: formData.startDate,
      endDate: formData.endDate,
    };

    row ? EditTrigger(modifiedData) : trigger(modifiedData);
  };

  return (
    <div>
      <Breadcrumb actionButtons={actionButtons} />
      <Tabs tabs={liveClassTabsData} />
      <Table>
        <TableHead rowCount={data?.data.length} headerData={tableColumnData} />
        <tbody className={`divide-y divide-gray-200`}>
          {!isLoading ? (
            <>
              {data?.data.map((row) => (
                <LiveClassesRow
                  key={row._id}
                  row={row}
                  mutate={mutate}
                  onEditClick={onEditClick}
                />
              ))}
            </>
          ) : (
            <tr>
              <td colSpan="100" className="text-center py-4">
                <Loader />
              </td>
            </tr>
          )}
        </tbody>
        <NoData isNotFound={!data?.data.length && !isLoading} />
      </Table>
      <Pagination
        totalRows={data?.pagination.totalItems || 0}
        currentPage={page}
        rowsPerPage={rowsPerPage}
        currentPath={location.pathname}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
      <Modal
        isOpen={isModalOpen}
        title={
          row
            ? "Edit Live Class Subscription"
            : "Create Live Class Subscription"
        }
        onConfirm={handleSubmit}
        onCancel={() => {
          setErrors({});
          setModalOpen(false);
        }}
        isLoading={isMutating || editMutating}
        formWidth
      >
        <LiveClassesForm
          errors={errors}
          handleSubmit={handleSubmit}
          onChange={handleInputChange}
          formData={formData}
          setModalOpen={setStorageModalOpen}
          setContentType={setContentType}
        />
      </Modal>
      <Modal
        isOpen={isStorageModalOpen}
        title={"Storage Manager"}
        // onConfirm={handleSubmit}
        onCancel={() => {
          setStorageModalOpen(false);
        }}
        footer={false}
      >
        <Storage
          isSelector
          onConfirm={(file) => {
            if (Array.isArray(file) && file.length)
              handleInputChange(contentType, file[0]?.fileUrl);
            setStorageModalOpen(false);
          }}
        />
      </Modal>
    </div>
  );
};

export default LiveClasses;
